// Tile-id enumeration, the split/merge tier table, the starting position -- the
// fixed vocabulary the world is authored in, as opposed to the numbers its
// behavior can be tuned by. Those are `physics.ts` now.
//
// Moved verbatim out of `game/src/scenes/GameScene.ts` (2026-07-30) as step 1a of
// the headless-simulation extraction. Deliberately excludes the *rendering*
// constants that sat beside these (TILE, MOVE_DURATION, TILESET_KEY, SPRITE_IDS):
// those describe how the world is drawn, not how it behaves, and stay in the
// Phaser scene.
import { Facing, MobProjectileId, MobVariantId } from './types';
import { DEFAULT_PHYSICS } from './physics';

// The tunables that used to be declared here, now aliases for their defaults
// (2026-07-31, Phase 1). They are still exported because "one regen tick's
// worth" is exactly what a test wants to say -- but nothing in the sim reads
// them at runtime any more: every rule reads `world.physics`, so a world given
// a tuned config actually runs on it. Reading these from a rule would be a
// silent opt-out of tuning, which is the one failure mode a PhysicsConfig has.
export const MANA_REGEN_PER_TICK = DEFAULT_PHYSICS.player.manaRegenPerTick;
export const MANA_REGEN_TICK_MS = DEFAULT_PHYSICS.player.manaRegenTickMs;
export const ROOM_DECAY_PER_TICK = DEFAULT_PHYSICS.room.decayPerTick;
export const ROOM_DECAY_TICK_MS = DEFAULT_PHYSICS.room.decayTickMs;
export const WATER_FREEZE_THRESHOLD = DEFAULT_PHYSICS.fluid.water.freezeThreshold;
export const WATER_MELT_THRESHOLD = DEFAULT_PHYSICS.fluid.water.meltThreshold;

/** Every kind of mob there is, read off the physics table rather than written
 * out again beside it -- a kind *is* a row of numbers there (see `MobVariantId`),
 * so this cannot fall behind the way a second hand-kept list would. What it is
 * for is the check `MobVariantId` cannot make: authored JSON has had no compiler
 * near it, and a mob whose kind nothing describes would stand still forever with
 * nothing to say why (see `rooms/load.ts`). */
export const MOB_VARIANT_IDS = Object.keys(DEFAULT_PHYSICS.mob) as MobVariantId[];

/** Everything a mob can throw (see `MobProjectileId`) -- the five spells plus
 * the Archer's arrow. Written out rather than derived, unlike the kinds above:
 * a projectile is not a row of numbers anywhere, it is a look, and the table it
 * would be derived from lives in `sim/mob.ts` where a UI has no business
 * reaching. The loader checks authored values against this for the same reason
 * it checks the kind. */
export const MOB_PROJECTILE_IDS: MobProjectileId[] = ['fire', 'ice', 'charge', 'decay', 'force', 'arrow'];

// Separate·Mass's split/merge tier ladder (see applySeparateEffect), keyed by
// mass.material ('stone' is every pre-existing mass object's
// implicit default). Tier 2 reuses a new, smaller texture; tier 3 reuses that
// same texture again, just scaled down further, rather than needing a third
// hand-drawn sprite. Split by material (2026-07-30, room9's Metal Tool/Scrap
// Metal; 2026-08-02, Wood) so a merge can yield "Metal Block" or "Wood Block"
// the same way it already yields "Boulder" -- splitRock/mergeRocks look this
// up via `target.material ?? 'stone'` rather than one single global table.
export const ROCK_TIER_META: Record<'stone' | 'metal' | 'wood', Record<1 | 2 | 3, { name: string; texture: string; spriteScale?: number; conductive?: boolean; description: string }>> = {
  stone: {
    1: {
      name: 'Boulder',
      texture: 'object-block',
      description:
        'A boulder easily big enough to block a doorway whole. Cast Separate + Mass on it and something in its structure gives, splitting it into two Rocks.',
    },
    2: {
      name: 'Rock',
      texture: 'object-rock',
      description:
        "A fist-sized fragment of the boulder it used to be. Separate + Mass splits it further into Pebbles -- or, with Reverse, two of these on adjacent tiles rejoin into a Boulder.",
    },
    3: {
      name: 'Pebble',
      texture: 'object-rock',
      spriteScale: 0.6,
      description:
        "The smallest a stone gets before mass stops meaning much. This won't split any further, but two Pebbles on adjacent tiles can still be reversed back into a Rock.",
    },
  },
  // Tier 3 used to mirror tier 2 exactly -- same name, same texture, no scale --
  // on the reasoning that nothing shipped ever split a metal piece that far.
  // That was wrong twice over: Separate·Mass has always allowed the split, and
  // an object the player cannot tell from another must not behave differently.
  // A tier-2 Scrap Metal splits and a tier-3 one refuses, and nothing on screen
  // said which you were holding. It now takes its own name and the same 0.6
  // scale Pebble and Kindling use (2026-08-11).
  metal: {
    1: {
      name: 'Metal Block',
      texture: 'object-block-metal',
      conductive: true,
      description: 'A block of dense, cool metal. Whatever runs through it doesn\'t stay put for long.',
    },
    2: {
      name: 'Scrap Metal',
      texture: 'object-scrap-metal',
      description: 'A jagged offcut of dense metal, snapped off of something larger a long time ago.',
    },
    3: {
      name: 'Metal Sliver',
      texture: 'object-scrap-metal',
      spriteScale: 0.6,
      description: "A thin splinter of metal, snapped off a larger offcut. This won't break down any further, but two Slivers on adjacent tiles can still be reversed back into Scrap Metal.",
    },
  },
  // room8's Wood Blocks are non-conductive conduit filler -- something to swap
  // *out* of a conduit path, not part of the charge system at all, so unlike
  // stone and metal this material never sets `conductive`. Tier 3 (Kindling)
  // reuses tier 2's texture scaled down, the same shortcut Pebble takes off
  // Rock, rather than the metal family's "just mirror tier 2" shortcut --
  // splitting a beam down to kindling is exactly the kind of thing this
  // material should be able to do, even if no shipped puzzle needs it yet.
  wood: {
    1: {
      name: 'Wood Block',
      texture: 'object-block-wood',
      description: 'A block of dry, sturdy wood. Cast Separate + Mass on it and it splits along the grain into two Split Beams.',
    },
    2: {
      name: 'Split Beam',
      texture: 'object-wood-beam',
      description:
        "A length of timber, split down from something larger. Separate + Mass splits it further into Kindling -- or, with Reverse, two of these on adjacent tiles rejoin into a Wood Block.",
    },
    3: {
      name: 'Kindling',
      texture: 'object-wood-beam',
      spriteScale: 0.6,
      description: "Thin enough to snap by hand. This won't split any further, but two pieces of Kindling on adjacent tiles can still be reversed back into a Split Beam.",
    },
  },
};

// Shared by every reformable ice object in room4 (see BarrierDef.meltStages) --
// least- to most-melted, the last entry matching exactly the meltsWhen
// threshold that makes it passable.
export const ICE_MELT_STAGES = ['object-iceblock', 'object-iceblock-cracked', 'object-iceblock-melting', 'object-iceblock-melted'];

export const FLOOR = 0;
export const WALL = 1;
export const DOOR = 2;
export const FLOOR_ROOM2 = 3;
export const FLOOR_ROOM3 = 4;
export const FLOOR_ROOM4 = 5;
export const FLOOR_ROOM0 = 6;
export const FLOOR_ROOM5 = 7;
export const FLOOR_ROOM6 = 8;
export const FLOOR_ROOM7 = 9;
export const FLOOR_ROOM8 = 10;
export const FLOOR_ROOM9 = 11;
export const FLOOR_ROOM10 = 12;

// A cliff face: impassable like WALL by default (see isWalkable), but
// climbable wherever a Vines object in a climbable stage (or, one day, a
// Ladder) currently occupies the same tile -- the same "conditionally
// passable, recomputed live" shape `barrier.reformable` already gives an ice
// barrier, just keyed off tile type instead of an object's own barrier.
//
// The two orientations are a rendering distinction only, not a mechanical
// one: which way a raised area's rock face reads as facing so a rectangular
// plateau's north and east edges both look right. Both tile ids are
// climbable by the exact same rule and, once climbed, passable in every
// direction -- there is no one-way-door behavior here.
export const CLIFF_NORTH = 13;
export const CLIFF_EAST = 14;

/** Nothing at all: a tile that is not part of the room.
 *
 * Every other tile id answers "what is the ground here"; this one says there is
 * no ground here, and the room simply does not extend this far. Impassable like
 * WALL (see isWalkable) and drawn as the background rather than as a surface,
 * which is the whole difference between the two: a wall is a thing the room has,
 * a void is the room stopping.
 *
 * It exists because a tilemap has no way to say "absent" -- every (x,y) in a
 * `cols`x`rows` grid holds an id, so a room whose footprint is not a rectangle
 * needs an id that means "outside the footprint". Without it the only way to
 * carve an L-shaped room was to fill the missing corner with wall, which reads
 * as a solid block of masonry sitting in a doorway rather than as the room
 * ending (2026-08-09).
 *
 * Does not block a Project, where a WALL does (see casts.ts's
 * isProjectPathBlocked): solid matter stops a beam and absence does not, and a
 * void is absence -- the same reading a Chasm object gets. */
export const VOID = 15;

export const DIRECTIONS: Facing[] = ['up', 'down', 'left', 'right'];

// room0 -- a tiny waking chamber -- is the real start now; room1 is where the old
// (2,2) spawn used to be, still reachable, just no longer the very first thing.
export const START_ROOM = 'room0';
export const START_X = 3;
export const START_Y = 4;
export const START_FACING: Facing = 'down';
export const DIRECTION_OFFSETS: Record<Facing, { x: number; y: number }> = {
  up: { x: 0, y: -1 },
  down: { x: 0, y: 1 },
  left: { x: -1, y: 0 },
  right: { x: 1, y: 0 },
};

// The caster's own reserves. Moved out of game/src/playerStats.ts (2026-07-30,
// sim extraction step 2) alongside the state they bound -- a ceiling on a
// simulated quantity is a simulation number, not a UI one -- and default-only
// aliases since Phase 1, same as the tunables above. The game's HUD reads the
// live values through playerStats.getMaxMana() and friends.
export const MAX_MANA = DEFAULT_PHYSICS.player.maxMana;
export const MAX_HEALTH = DEFAULT_PHYSICS.player.maxHealth;
export const MAX_CHARGE = DEFAULT_PHYSICS.player.maxCharge;
